import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import get from "lodash/get";
import isNil from "lodash/isNil";
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { useWindowSize } from '../../../../utils/hooks/use-window-size';
import { Spinner } from '../../../../decorators/spinner';
import { getFormattedTime } from './helpers/helpers';
import { TimeData } from './data/time-data';

const TimePicker = ({
  value,
  setValue,
  startTimeOnly,
  endTimeOnly,
  isDisabled,
  intl,
  getDayAvailability,
  timeAvailabilityLoading,
  startTimeAvailability,
  endTimeAvailability
}) => {
  const [startTimeActive, setStartTimeActive] = useState(false);
  const [endTimeActive, setEndTimeActive] = useState(false);
  const [mobileHeight, setMobileHeight] = useState();
  const windowDims = useWindowSize(false);

  const createOptions = (timeObjs, availability) => {
    return (timeObjs || []).map((timeObj) => {
      const disabled = !!find(availability, { time_index: timeObj.time_index, available: false });
      return {
        label: getFormattedTime(timeObj, intl),
        value: timeObj,
        disabled
      };
    });
  };

  const startTimes = useMemo(() => {
    if (endTimeOnly) {
      return;
    }
    return createOptions(TimeData.slice(0, 36), startTimeAvailability);
  }, [startTimeAvailability]);

  const endTimes = useMemo(() => {
    if (startTimeOnly) {
      return;
    }
    if (!value?.startTime) {
      return createOptions(TimeData.slice(0, 37));
    }
    const startTimeIndex = findIndex(TimeData, (time) => time.time_index === value?.startTime);
    return createOptions(TimeData.slice(startTimeIndex + 1, 37), endTimeAvailability);
  }, [value?.startTime, endTimeAvailability]);

  useEffect(() => {
    if (!windowDims?.height || mobileHeight) {
      return;
    }
    const baseHeight = windowDims?.height ? `${windowDims.height}px` : '100vh';
    setMobileHeight(`calc(${baseHeight} - 115px)`);
  }, [windowDims?.height]);

  const handleStartTimeChange = (newStartTime) => {
    if (newStartTime.disabled) return;
    let endTimeIndex = value?.endTime;
    const startTimeIndex = get(newStartTime, 'value.time_index');
    if (startTimeIndex >= endTimeIndex && !isNil(endTimeIndex)) {
      endTimeIndex = null;
    }
    setValue({
      startTime: startTimeIndex,
      endTime: endTimeIndex
    });
  };

  const handleEndTimeChange = (newEndTime) => {
    if (newEndTime.disabled) return;
    const endTimeIndex = get(newEndTime, 'value.time_index');
    setValue({
      startTime: value?.startTime,
      endTime: endTimeIndex
    });
  };

  useEffect(() => {
    const clickoutListener = () => {
      setStartTimeActive(false);
    };
    if (startTimeActive) {
      setEndTimeActive(false);
      window.addEventListener('click', clickoutListener);
    }
    return () => window.removeEventListener('click', clickoutListener);
  }, [startTimeActive]);

  useEffect(() => {
    const clickoutListener = () => {
      setEndTimeActive(false);
    };
    if (endTimeActive) {
      setStartTimeActive(false);
      window.addEventListener('click', clickoutListener);
    }
    return () => window.removeEventListener('click', clickoutListener);
  }, [endTimeActive]);

  useEffect(() => {
    if (startTimeOnly) {
      getDayAvailability('start', false);
    } else if (endTimeOnly) {
      getDayAvailability('end', false);
    }
  }, []);

  return (
    <div
      className={classNames('mobile_timepicker_wrap responsive-padding', { is_disabled: isDisabled })}
      style={{ maxHeight: mobileHeight }}
    >
      {timeAvailabilityLoading
        ? <div className="spinner-container">
          <Spinner />
        </div>
        : <>
          {(startTimeOnly || !endTimeOnly) &&
            startTimes.map((timeObj) => (
              <div
                key={timeObj.label}
                className="mobile_timepicker_btn_wrap"
              >
                <button
                  className={classNames('btn-clear', { disabled: timeObj.disabled })}
                  onClick={(e) => {
                    e.preventDefault();
                    handleStartTimeChange(timeObj);
                  }}
                >
                  {timeObj.label}
                </button>
              </div>
            ))}
          {(endTimeOnly || !startTimeOnly) &&
            endTimes.map((timeObj) => (
              <div
                key={timeObj.label}
                className="mobile_timepicker_btn_wrap"
              >
                <button
                  className={classNames('btn-clear', { disabled: timeObj.disabled })}
                  onClick={(e) => {
                    e.preventDefault();
                    handleEndTimeChange(timeObj);
                  }}
                >
                  {timeObj.label}
                </button>
              </div>
            ))}
        </>
      }
    </div>
  );
};

TimePicker.propTypes = {
  value: PropTypes.exact({
    startTime: PropTypes.exact({
      label: PropTypes.string,
      value: PropTypes.exact({
        h: PropTypes.number,
        m: PropTypes.number,
        overnight: PropTypes.bool
      })
    }),
    endTime: PropTypes.exact({
      label: PropTypes.string,
      value: PropTypes.exact({
        h: PropTypes.number,
        m: PropTypes.number,
        overnight: PropTypes.bool
      })
    })
  }),
  setValue: PropTypes.func,
  startTimeOnly: PropTypes.bool,
  endTimeOnly: PropTypes.bool,
  isDisabled: PropTypes.bool,
  getDayAvailability: PropTypes.func,
  timeAvailabilityLoading: PropTypes.bool,
  startTimeAvailability: PropTypes.array,
  endTimeAvailability: PropTypes.array,
  intl: intlShape
};

exports.TimePicker = injectIntl(TimePicker);
